(() => {
  const cfgEl = document.getElementById('cart-gift-config');
  if (!cfgEl) return; // gift disabled or not rendered on this page
  const cfg = JSON.parse(cfgEl.textContent);

  const STORE_KEY = 'cart-gift-state';
  const PRODUCT_MSG_FALLBACK = 'Add {product} to get a free gift!';

  const DRAWER_SELECTORS = ['#CartDrawer-TotalQty', '.wt-cart__drawer__body', '.wt-cart__drawer__footer'];
  const HEADER_SELECTORS = ['#cart-icon-bubble', '[data-cart-count]', '.js-cart-count'];

  const rawFetch = window.fetch.bind(window);
  let busy = false;
  let last = null; 
  let syncTimer;
  let restoreTimer;

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    }).format(cents / 100);

  const isGift = (item) => !!(item.properties && item.properties._gift === 'true');
  // Never counted toward the threshold: the gift, gift cards, and excluded products (e.g. gift wrap).
  const isExcluded = (item) =>
    isGift(item)|| item.variant_id === Number(cfg.variantId) || item.gift_card === true || cfg.excludeProductIds.includes(item.product_id);

  const getCart = async () => (await rawFetch('/cart.js')).json();
  const post = (url, body) =>
    rawFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const loadState = (token) => {
    try {
      const s = JSON.parse(localStorage.getItem(STORE_KEY)) || {};
      return s.token === token ? s : { token };
    } catch (e) {
      return { token };
    }
  };
  const saveState = (s) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {}
  };

  async function refreshCart() {
    if (location.pathname === '/cart') return location.reload();
    try {
      const base = (window.routes && window.routes.cart_url) || '/cart';
      const headerEls = HEADER_SELECTORS.map((sel) => [sel, document.querySelector(sel)]).filter(([, el]) => el);

      const [drawerRes, pageRes] = await Promise.all([
        rawFetch(`${base}?section_id=cart-drawer`),
        headerEls.length ? rawFetch(location.href, { cache: 'no-store' }) : null,
      ]);
      const parse = async (r) => new DOMParser().parseFromString(await r.text(), 'text/html');
      const drawerDoc = await parse(drawerRes);

      DRAWER_SELECTORS.forEach((sel) => {
        const fresh = drawerDoc.querySelector(sel);
        const current = document.querySelector(sel);
        if (fresh && current) current.replaceWith(fresh);
      });
      if (pageRes) {
        const pageDoc = await parse(pageRes);
        headerEls.forEach(([sel, current]) => {
          const fresh = pageDoc.querySelector(sel);
          if (fresh) current.innerHTML = fresh.innerHTML;
        });
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('wt-cart--empty', !drawerDoc.querySelector('.wt-cart__list'));
    } catch (e) {
      console.error('[cart-gift] refresh failed', e);
    }
  }

  function evaluate(cart) {
    const real = cart.items.filter((i) => !isGift(i));
    const subtotal = cart.items.filter((i) => !isExcluded(i)).reduce((s, i) => s + i.final_line_price, 0);
    const qualifies = cfg.mode
      ? subtotal >= cfg.threshold
      : real.some((i) => i.product_id === cfg.conditionProductId);
    return { subtotal, qualifies, hasItems: real.length > 0 };
  }

  function renderGift(state) {
    const box = document.querySelector('[data-cart-gift]');
    if (!box) return;
    box.dataset.rendered = '1';
    const msg = box.querySelector('[data-gift-msg]');
    const fill = box.querySelector('[data-gift-fill]');
    const restore = box.querySelector('[data-gift-restore]');
    const unlocked = state.qualifies;

    box.hidden = !state.hasItems;
    box.classList.toggle('is-unlocked', unlocked);

    let text;
    if (unlocked) {
      text = state.giftPresent ? cfg.msgAfter : cfg.msgRemoved;
    } else {
      let template = cfg.mode ? cfg.msgBefore : cfg.msgBeforeProduct;
      if (!cfg.mode && (!template || template.includes('{amount}'))) template = PRODUCT_MSG_FALLBACK;
      text = (template || '')
        .replace('{amount}', money(Math.max(cfg.threshold - state.subtotal, 0)))
        .replace('{product}', cfg.conditionProductTitle || '');
    }
    msg.textContent = text;
    msg.hidden = !text;
    if (restore) restore.hidden = !(unlocked && !state.giftPresent);
    if (fill && cfg.mode) fill.style.width = Math.min((state.subtotal / cfg.threshold) * 100, 100) + '%';
  }

  // Upsell: threshold mode only, and only while adding it would unlock the gift
  function renderUpsell(cart, state) {
    const el = document.querySelector('[data-cart-upsell]');
    if (!el) return;
    el.dataset.rendered = '1';
    if (!cfg.mode) { el.hidden = true; return; }
    const inCart = cart.items.some((i) => String(i.product_id) === el.dataset.productId);
    const from = Math.max(cfg.threshold - cfg.upsellPrice, 0);
    el.hidden = inCart || !state.hasItems || state.subtotal < from || state.subtotal >= cfg.threshold;
  }

  async function sync(forceRefresh = false) {
    if (busy) return;
    busy = true;
    try {
      const cart = await getCart();
      const state = evaluate(cart);
      // Raising the gift's qty can make Shopify split it into several _gift lines (free + full price)
      const giftLines = cart.items.filter(isGift);
      const gift = giftLines[0];
      const giftQty = giftLines.reduce((s, i) => s + i.quantity, 0);
      const st = loadState(cart.token);
      let changed = false;
      let giftPresent = !!gift;

      if (gift) {
        st.seen = true;
        st.removed = false;
        const clearGifts = () =>
          post('/cart/update.js', { updates: Object.fromEntries(giftLines.map((i) => [i.key, 0])) });
        if (!state.qualifies) {
          await clearGifts();
          st.seen = false;
          giftPresent = false;
          changed = true;
        } else if (giftQty !== 1) {
          // Keep a single free unit; extra units go to the regular (paid) line of the same variant
          await clearGifts();
          const items = [{ id: gift.variant_id, quantity: 1, properties: { _gift: 'true' } }];
          if (giftQty > 1) items.unshift({ id: gift.variant_id, quantity: giftQty - 1 });
          await post('/cart/add.js', { items });
          changed = true;
        }
      } else if (state.qualifies) {
        if (st.seen) { st.removed = true; st.seen = false; } // customer removed it: keep it removed
        if (!st.removed) {
          const res = await post('/cart/add.js', {
            items: [{ id: cfg.variantId, quantity: 1, properties: { _gift: 'true' } }],
          });
          if (res.ok) { st.seen = true; giftPresent = true; changed = true; }
        }
      } else {
        st.removed = false; // no longer qualifies: can be earned again
        st.seen = false;
      }

      // Gift line removed, but Shopify's automatic discount moved onto a paid line of the same variant:
      if (!giftPresent && state.qualifies) {
        giftPresent = cart.items.some(
          (i) => !isGift(i) && i.variant_id === Number(cfg.variantId) && i.final_line_price < i.original_line_price
        );
      }

      saveState(st);
      if (changed || forceRefresh) await refreshCart();
      state.giftPresent = giftPresent;
      last = { cart, state };
      renderGift(state);
      renderUpsell(cart, state);
    } catch (e) {
      console.error('[cart-gift]', e);
    } finally {
      busy = false;
    }
  }

  const scheduleSync = () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 200);
  };
  window.fetch = function (...args) {
    const p = rawFetch(...args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    if (/\/cart\/(add|change|update|clear)/.test(url)) p.then(scheduleSync).catch(() => {});
    return p;
  };

  // upsell add + "Add free gift back"
  document.addEventListener('click', async (e) => {
    const upsellBtn = e.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      upsellBtn.disabled = true;
      try {
        await post('/cart/add.js', { items: [{ id: Number(upsellBtn.dataset.variantId), quantity: 1 }] });
        await sync(true);
      } finally {
        upsellBtn.disabled = false;
      }
      return;
    }
    if (e.target.closest('[data-gift-restore]')) {
      const cart = await getCart();
      const st = loadState(cart.token);
      st.removed = false;
      st.seen = false;
      saveState(st);
      await sync();
    }
  });

  new MutationObserver(() => {
    clearTimeout(restoreTimer);
    restoreTimer = setTimeout(() => {
      if (!document.querySelector('[data-cart-gift]:not([data-rendered]), [data-cart-upsell]:not([data-rendered])')) return;
      if (last) { renderGift(last.state); renderUpsell(last.cart, last.state); } else sync();
    }, 30);
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => sync());
  else sync();
})();